/**
 * Main Voice Service orchestrator.
 * Uses Deepgram Flux for real-time conversational STT.
 * Falls back to on-device Whisper tiny.en when offline.
 * Uses native speechSynthesis for TTS.
 *
 * Push-to-talk: press mic → stream audio to Deepgram → Flux detects turns →
 * press mic again to stop. TTS pauses mic during playback.
 */

import { loadWhisperModel, transcribe, destroyWhisperModel, isWhisperLoaded } from './WhisperSTT';
import { startVAD, stopVAD, isVADRunning, pauseVAD, resumeVAD } from './VADService';
import { speak, stop as stopTTS, isTTSSupported, getAvailableVoices } from './TTSPlayer';
import { kokoroTTS } from './KokoroWebSocketTTS';
import { DeepgramFluxSTT } from './DeepgramFluxSTT';
import { getDeepgramToken } from '@/services/api/voiceApi';
import type {
  VoiceState,
  VoiceEnrollment,
  VoicePreferences,
  VoiceTranscription,
  ModelLoadingProgress,
} from './types';

export interface VoiceServiceCallbacks {
  onStateChange: (state: VoiceState) => void;
  onModelProgress?: (progress: ModelLoadingProgress) => void;
  onTranscription?: (result: VoiceTranscription) => void;
  onInterimTranscript?: (text: string) => void;
  onVerificationFailed?: (confidence: number) => void;
  onError?: (error: string) => void;
}

let ready = false;
let offlineMode = false;
let deepgramToken: string | null = null;
let deepgramSTT: DeepgramFluxSTT | null = null;
let mediaStream: MediaStream | null = null;
let audioContext: AudioContext | null = null;
let audioWorkletNode: ScriptProcessorNode | null = null;
let listening = false;
let enrollment: VoiceEnrollment | null = null;
let callbacks: VoiceServiceCallbacks | null = null;
let preferences: VoicePreferences = {
  enabled: false,
  ttsVoiceName: '',
  ttsSpeed: 1.0,
  autoPlayTTS: true,
  useKokoroTTS: false,
  kokoroAgentId: undefined,
};

/**
 * Initialize voice service: fetch Deepgram token.
 * Falls back to loading Whisper model if token fetch fails.
 */
export async function initVoiceService(
  cbs: VoiceServiceCallbacks,
  enrolledProfile: VoiceEnrollment | null,
): Promise<void> {
  callbacks = cbs;
  enrollment = enrolledProfile;

  if (ready) return;

  try {
    const response = await getDeepgramToken();
    if (response.success && response.data?.token) {
      deepgramToken = response.data.token;
      offlineMode = false;
      ready = true;
      callbacks.onStateChange('idle');
      console.log('[VoiceService] Deepgram token acquired, ready for streaming STT');
    } else {
      throw new Error('No token in response');
    }
  } catch (error) {
    console.warn('[VoiceService] Failed to get Deepgram token, loading Whisper fallback:', error);
    offlineMode = true;
    callbacks.onStateChange('loading-models');
    await loadWhisperModel(callbacks.onModelProgress);
    ready = true;
    callbacks.onStateChange('idle');
    console.log('[VoiceService] Whisper fallback loaded');
  }
}

/**
 * Start listening: open mic and stream audio to Deepgram Flux.
 * Falls back to VAD + Whisper if offline.
 */
export async function startListening(): Promise<void> {
  if (!ready || !callbacks) {
    throw new Error('VoiceService not initialized');
  }

  // Stop any current TTS
  stopTTS();

  if (offlineMode || !deepgramToken) {
    await startOfflineFallback();
    return;
  }

  try {
    callbacks.onStateChange('listening');

    // Open microphone
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    // Set up audio processing to get raw PCM frames
    // Note: browser may not honor 16kHz request — use actual rate and tell Deepgram
    audioContext = new AudioContext({ sampleRate: 16000 });
    const actualSampleRate = audioContext.sampleRate;
    console.log('[VoiceService] AudioContext sample rate:', actualSampleRate);
    const source = audioContext.createMediaStreamSource(mediaStream);

    // ScriptProcessorNode to capture raw audio frames
    audioWorkletNode = audioContext.createScriptProcessor(4096, 1, 1);

    // Create Deepgram connection with actual sample rate
    deepgramSTT = new DeepgramFluxSTT();
    deepgramSTT.setCallbacks({
      onStartOfTurn: () => {
        callbacks?.onStateChange('speech-detected');
      },
      onTranscript: (text: string, isFinal: boolean) => {
        if (text) {
          // Interim transcripts update the UI but don't trigger send
          callbacks?.onInterimTranscript?.(text);
        }
      },
      onEndOfTurn: (transcript: string) => {
        if (transcript) {
          // Final transcript — this triggers the send
          callbacks?.onTranscription?.({
            text: transcript,
            confidence: 1.0,
            language: 'en',
          });
        }
        // Return to listening state — continuous mode
        if (listening) {
          callbacks?.onStateChange('listening');
        }
      },
      onError: (error: Error) => {
        console.error('[VoiceService] Deepgram error:', error);
        callbacks?.onError?.(error.message);
      },
      onClose: () => {
        if (listening) {
          console.log('[VoiceService] Deepgram connection closed while listening');
        }
      },
    });

    await deepgramSTT.connect(deepgramToken, actualSampleRate);

    // Pipe audio to Deepgram
    audioWorkletNode.onaudioprocess = deepgramAudioHandler;

    source.connect(audioWorkletNode);
    audioWorkletNode.connect(audioContext.destination);

    listening = true;
  } catch (error) {
    console.error('[VoiceService] Failed to start Deepgram, falling back:', error);
    cleanupAudio();
    await startOfflineFallback();
  }
}

/**
 * Stop listening and clean up.
 */
export async function stopListening(): Promise<void> {
  listening = false;
  cleanupAudio();

  // Also stop VAD if it was running (offline mode)
  if (isVADRunning()) {
    await stopVAD();
  }

  callbacks?.onStateChange('idle');
}

/**
 * Cancel listening without processing.
 */
export async function cancelListening(): Promise<void> {
  await stopListening();
}

function cleanupAudio(): void {
  if (audioWorkletNode) {
    audioWorkletNode.disconnect();
    audioWorkletNode.onaudioprocess = null;
    audioWorkletNode = null;
  }

  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }

  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop());
    mediaStream = null;
  }

  if (deepgramSTT) {
    deepgramSTT.disconnect();
    deepgramSTT = null;
  }
}

/**
 * Offline fallback: use VAD + Whisper (on-device).
 */
async function startOfflineFallback(): Promise<void> {
  // Ensure Whisper is loaded
  if (!isWhisperLoaded()) {
    callbacks?.onStateChange('loading-models');
    await loadWhisperModel(callbacks?.onModelProgress);
  }

  callbacks?.onStateChange('listening');

  await startVAD({
    onSpeechStart: () => {
      callbacks?.onStateChange('speech-detected');
    },
    onSpeechEnd: async (audio: Float32Array) => {
      try {
        callbacks?.onStateChange('transcribing');
        const transcription = await transcribe(audio);

        if (transcription.text) {
          callbacks?.onTranscription?.(transcription);
        }

        if (isVADRunning()) {
          callbacks?.onStateChange('listening');
        } else {
          callbacks?.onStateChange('idle');
        }
      } catch (error) {
        console.error('[VoiceService] Whisper transcription error:', error);
        callbacks?.onError?.(String(error));
        if (isVADRunning()) {
          callbacks?.onStateChange('listening');
        } else {
          callbacks?.onStateChange('error');
        }
      }
    },
    onVADMisfire: () => {
      console.log('[VoiceService] VAD misfire, still listening...');
    },
  });
}

/**
 * TTS playback with barge-in detection.
 *
 * During TTS:
 *  - Audio is NOT sent to Deepgram (no cost, no echo transcription)
 *  - Mic stays open with a lightweight RMS energy monitor
 *  - Browser echoCancellation filters most speaker bleed (typically < 0.01 RMS)
 *  - If real speech is detected (RMS > threshold), TTS stops and Deepgram resumes
 *
 * After TTS finishes naturally:
 *  - Normal Deepgram audio streaming resumes automatically
 */
let ttsPlaying = false;

// Barge-in tuning: RMS energy threshold.
// With echoCancellation enabled, speaker bleed is typically < 0.01-0.015 RMS.
// Real human speech at conversational distance is typically > 0.03 RMS.
const BARGE_IN_RMS_THRESHOLD = 0.03;
// Require consecutive frames above threshold to prevent single-frame spikes
const BARGE_IN_CONSECUTIVE_FRAMES = 3;
let bargeInFrameCount = 0;

/**
 * The normal handler that streams audio to Deepgram.
 * Extracted so we can swap it in/out cleanly.
 */
function deepgramAudioHandler(event: AudioProcessingEvent): void {
  const inputData = event.inputBuffer.getChannelData(0);
  deepgramSTT?.sendAudio(new Float32Array(inputData));
}

/**
 * Barge-in monitor: checks RMS energy but does NOT send audio to Deepgram.
 * If the user speaks over TTS, stops TTS and resumes Deepgram streaming.
 */
function bargeInMonitorHandler(event: AudioProcessingEvent): void {
  const input = event.inputBuffer.getChannelData(0);

  // Calculate RMS energy of this audio frame
  let sum = 0;
  for (let i = 0; i < input.length; i++) {
    sum += input[i] * input[i];
  }
  const rms = Math.sqrt(sum / input.length);

  if (rms > BARGE_IN_RMS_THRESHOLD) {
    bargeInFrameCount++;
    if (bargeInFrameCount >= BARGE_IN_CONSECUTIVE_FRAMES) {
      // Real speech detected — barge in
      handleBargeIn();
    }
  } else {
    bargeInFrameCount = 0;
  }
  // Intentionally NOT sending audio to Deepgram during TTS
}

export async function speakResponse(text: string): Promise<void> {
  if (!preferences.autoPlayTTS) return;

  // Pause VAD if in offline mode
  const vadWasRunning = isVADRunning();
  if (vadWasRunning) {
    pauseVAD();
  }

  ttsPlaying = true;
  bargeInFrameCount = 0;
  callbacks?.onStateChange('speaking');

  // Swap to barge-in monitor — stops sending audio to Deepgram, monitors for speech
  if (listening && audioWorkletNode) {
    audioWorkletNode.onaudioprocess = bargeInMonitorHandler;
    console.log('[VoiceService] TTS playing — Deepgram paused, barge-in monitor active');
  }

  const onEnd = async () => {
    ttsPlaying = false;
    bargeInFrameCount = 0;
    // Resume normal Deepgram streaming
    if (listening && audioWorkletNode) {
      audioWorkletNode.onaudioprocess = deepgramAudioHandler;
      console.log('[VoiceService] TTS finished — Deepgram resumed');
      callbacks?.onStateChange('listening');
    } else if (vadWasRunning && isVADRunning()) {
      await resumeVAD();
      callbacks?.onStateChange('listening');
    } else {
      callbacks?.onStateChange('idle');
    }
  };

  const onError = async () => {
    ttsPlaying = false;
    bargeInFrameCount = 0;
    if (listening && audioWorkletNode) {
      audioWorkletNode.onaudioprocess = deepgramAudioHandler;
      callbacks?.onStateChange('listening');
    } else if (vadWasRunning && isVADRunning()) {
      await resumeVAD();
      callbacks?.onStateChange('listening');
    } else {
      callbacks?.onStateChange('idle');
    }
  };

  if (preferences.useKokoroTTS) {
    await kokoroTTS.speak(text, {
      agentId: preferences.kokoroAgentId,
      onEnd,
      onError,
    });
  } else {
    if (!isTTSSupported()) { await onError(); return; }
    await speak(text, {
      voiceName: preferences.ttsVoiceName || undefined,
      rate: preferences.ttsSpeed,
      onEnd,
      onError,
    });
  }
}

/**
 * Barge-in: user is speaking over TTS.
 * Stop TTS immediately and resume Deepgram streaming so their speech is transcribed.
 */
function handleBargeIn(): void {
  if (!ttsPlaying) return;

  console.log('[VoiceService] Barge-in detected — stopping TTS, resuming Deepgram');
  stopTTS();
  ttsPlaying = false;
  bargeInFrameCount = 0;

  // Resume normal Deepgram streaming
  if (listening && audioWorkletNode) {
    audioWorkletNode.onaudioprocess = deepgramAudioHandler;
  }

  callbacks?.onStateChange('speech-detected');
}

export function isTTSActive(): boolean {
  return ttsPlaying;
}

/**
 * Stop TTS playback (user clicked stop button).
 */
export async function stopSpeaking(): Promise<void> {
  stopTTS();
  ttsPlaying = false;
  bargeInFrameCount = 0;

  if (listening && audioWorkletNode) {
    audioWorkletNode.onaudioprocess = deepgramAudioHandler;
    callbacks?.onStateChange('listening');
  } else if (isVADRunning()) {
    await resumeVAD();
    callbacks?.onStateChange('listening');
  } else {
    callbacks?.onStateChange('idle');
  }
}

/**
 * Update enrollment data.
 */
export function setEnrollment(newEnrollment: VoiceEnrollment | null): void {
  enrollment = newEnrollment;
}

/**
 * Update TTS preferences.
 */
export function setPreferences(prefs: Partial<VoicePreferences>): void {
  preferences = { ...preferences, ...prefs };
}

/**
 * Get available TTS voices for settings UI.
 */
export async function getVoices(): Promise<SpeechSynthesisVoice[]> {
  return getAvailableVoices();
}

/**
 * Check if voice service is ready.
 */
export function isReady(): boolean {
  return ready;
}

/**
 * Check if currently listening.
 */
export function isListening(): boolean {
  return listening || isVADRunning();
}

/**
 * Cleanup all resources.
 */
export function destroyVoiceService(): void {
  listening = false;
  cleanupAudio();
  stopVAD();
  stopTTS();
  destroyWhisperModel();
  ready = false;
  offlineMode = false;
  deepgramToken = null;
  enrollment = null;
  callbacks = null;
}
